<template>
  <span v-if="syncStatus">
    <!-- The :key forces a remount when the ongoing state flips, so the icon
         slot is re-evaluated (BaseButton doesn't reactively remove the slot
         wrapper when its content becomes empty). -->
    <BaseButton
      :key="`user-sync-status--ongoing-${isOngoing}`"
      size="tiny"
      :theme="isOngoing ? 'blue' : 'default'"
      :outline="!isOngoing"
      rounded
      class="user-sync-status"
      @click="openDialog"
    >
      <template v-if="isOngoing" #icon>
        <SyncIcon class="user-sync-status__icon" />
      </template>
      {{ chipLabel }}
    </BaseButton>

    <UploadStatusModal
      v-if="isOpen"
      :user-upload="syncStatus"
      @close="closeDialog"
    />
  </span>
</template>

<script setup lang="ts">
import BaseButton from '@/components/generics/base/BaseButton.vue'
import SyncIcon from '@/components/icon_components/SyncIcon.vue'
import { useCurrentCompany } from '@/hooks/swr/useCurrentCompany'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat'
import { useDialog } from '@/hooks/useDialog'
import { useI18n } from '@/hooks/useI18N'
import { useToast } from '@/hooks/useToast'
import {
  useUserUploadQuery,
  useUserUploadsListQuery,
} from '@/queries/userUpload/userUploadQueries'
import { computed, watch } from 'vue'
import UploadStatusModal from './UploadStatusModal.vue'

const i18n = useI18n()
const { formatDateTime } = useDateTimeFormat()
const { isOpen, openDialog, closeDialog } = useDialog()
const { doToast } = useToast()
const { data: company } = useCurrentCompany()
const companyNameKey = computed(() => company.value?.name_key)

const { data: uploadsList } = useUserUploadsListQuery(companyNameKey, {
  sort: '-created_at',
  pageSize: 1,
})

const latestUploadId = computed(() => uploadsList.value?.[0]?.id)

const { data: polledUpload } = useUserUploadQuery(
  companyNameKey,
  latestUploadId
)

const syncStatus = computed(
  () => polledUpload.value ?? uploadsList.value?.[0] ?? null
)

const isOngoing = computed(() => {
  const state = syncStatus.value?.state
  return state === 'initialized' || state === 'started'
})

const chipLabel = computed(() => {
  if (isOngoing.value) return i18n.t('userUpload.SYNC_UPLOAD_ONGOING')
  return i18n.t('userUpload.SYNC_LAST_UPLOAD', {
    date: formatDateTime(syncStatus.value?.ended_at),
  })
})

watch(
  () => polledUpload.value?.state,
  (newState, prevState) => {
    const wasOngoing = prevState === 'initialized' || prevState === 'started'
    const isTerminal = newState === 'stopped' || newState === 'halted'
    if (wasOngoing && isTerminal) {
      doToast({ text: i18n.t('userUpload.SYNC_UPLOAD_COMPLETE_TOAST') })
    }
  }
)
</script>

<style lang="scss" scoped>
.user-sync-status {
  white-space: nowrap;

  &__icon {
    animation: spin 3s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
